const DB_NAME = 'musictutor-v1';
let promise;
function database() {
  if (!promise) promise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      db.createObjectStore('projects', { keyPath: 'id' });
      db.createObjectStore('settings');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => { promise = null; reject(req.error); };
    req.onblocked = () => { promise = null; reject(new Error('Close other PianoTutor tabs and try again.')); };
  });
  return promise;
}
async function transaction(store, mode, fn) {
  const db = await database();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const result = fn(tx.objectStore(store));
    tx.oncomplete = () => resolve(result?.result);
    tx.onabort = tx.onerror = () => reject(tx.error || new Error('Your browser could not save this change.'));
  });
}
export const localStorageBackend = {
  projects: () => transaction('projects', 'readonly', s => s.getAll()),
  save: project => transaction('projects', 'readwrite', s => s.put(structuredClone(project))),
  remove: id => transaction('projects', 'readwrite', s => s.delete(id)),
  settings: () => transaction('settings', 'readonly', s => s.get('preferences')),
  saveSettings: settings => transaction('settings', 'readwrite', s => s.put(structuredClone(settings), 'preferences')),
  import: projects => transaction('projects', 'readwrite', s => { for (const project of projects) s.put(project); }),
};

export class AccountStorage {
  constructor(local = localStorageBackend, request = (...args) => fetch(...args), timeouts = {}) {
    this.local = local; this.request = request; this.mode = 'pending'; this.session = null; this.queue = Promise.resolve(); this.writeErrors = new Map(); this.pendingWrites = new Map();
    this.timeouts = { session: 10000, request: 120000, ...timeouts };
  }
  async api(path, method = 'GET', body, account = this.session?.user?.id) {
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), path === '/api/session' ? this.timeouts.session : this.timeouts.request);
    try {
      let response;
      try { response = await this.request(path, { method, credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { ...(body === undefined ? {} : { 'Content-Type': 'application/json' }), ...(account ? { 'X-MusicTutor-Account': account } : {}) }, ...(body === undefined ? {} : { body: JSON.stringify(body) }) }); }
      catch { throw new Error('Cloud storage could not be reached. Check your connection and try again.'); }
      let value; try { value = await response.json(); } catch { throw new Error('Account service is unavailable. Reload to try again.'); }
      if (!response.ok) throw Object.assign(new Error(value.error || 'Cloud storage is unavailable. Please try again.'), { code: value.code, configured: value.configured });
      return value;
    } catch (error) {
      if (controller.signal.aborted) throw new Error('The account service did not respond in time. Check your connection and try again.');
      throw error;
    } finally { clearTimeout(timeout); }
  }
  async initialize() {
    this.mode = 'pending';
    let session;
    try { session = await this.api('/api/session'); }
    catch (error) { if (error.code === 'session_expired') this.session = { configured: error.configured, user: null, reauthenticate: true }; throw error; }
    this.session = session;
    this.mode = session.user ? session.initialized ? 'cloud' : 'onboarding' : 'local';
    return session;
  }
  async startAccount(choice) {
    if (this.mode !== 'onboarding') throw new Error('Account setup is not pending.');
    const data = { choice };
    if (choice === 'migrate') [data.projects, data.settings] = await Promise.all([this.local.projects(), this.local.settings()]);
    await this.api('/api/account/initialize', 'POST', data);
    this.session.initialized = true; this.mode = 'cloud';
  }
  async logout() {
    await this.flush();
    await this.api('/api/logout', 'POST', {});
    // The app reloads to replace all in-memory account state before local reads.
    this.mode = 'pending';
  }
  async flush() { await this.queue; if (this.writeErrors.size) throw this.writeErrors.values().next().value; }
  operation(name, args, path, method = 'GET', body) {
    if (this.mode === 'local') return this.local[name](...args);
    if (this.mode !== 'cloud') return Promise.reject(new Error('Open Account to finish connecting your storage.'));
    const account = this.session.user.id;
    if (method === 'GET') {
      // A read observes the writes preceding it, before subsequent edits.
      this.pendingWrites.clear();
      const result = this.queue.then(() => this.api(path, method, undefined, account));
      this.queue = result.then(() => {}, () => {});
      return result;
    }
    const keys = name === 'import' ? body.map(project => `project:${project.id}`) : [name === 'save' ? `project:${body.id}` : name === 'remove' ? `project:${args[0]}` : 'settings'];
    const coalesces = name === 'save' || name === 'saveSettings';
    const pending = coalesces && this.pendingWrites.get(keys[0]);
    if (pending && pending.account === account) {
      // Slow connections retain one pending snapshot per record, rather than
      // every obsolete playback position. All callers wait for that latest save.
      pending.body = structuredClone(body);
      return pending.result;
    }
    for (const key of keys) this.pendingWrites.delete(key);
    const entry = { body: structuredClone(body), account };
    const result = this.queue.then(() => {
      if (this.pendingWrites.get(keys[0]) === entry) this.pendingWrites.delete(keys[0]);
      return this.api(path, method, entry.body, account);
    });
    entry.result = result;
    if (coalesces) this.pendingWrites.set(keys[0], entry);
    this.queue = result.then(() => { for (const key of keys) this.writeErrors.delete(key); }, error => { for (const key of keys) this.writeErrors.set(key, error); });
    return result;
  }
  projects() { return this.operation('projects', [], '/api/storage/projects'); }
  settings() { return this.operation('settings', [], '/api/storage/settings'); }
  save(project) { return this.operation('save', [project], '/api/storage/projects', 'PUT', project); }
  remove(id) { return this.operation('remove', [id], `/api/storage/projects/${encodeURIComponent(id)}`, 'DELETE'); }
  saveSettings(settings) { return this.operation('saveSettings', [settings], '/api/storage/settings', 'PUT', settings); }
  import(projects) { return this.operation('import', [projects], '/api/storage/import', 'POST', projects); }
}
export const storage = new AccountStorage();
